import { useMutation, useQuery } from "react-query";
import { HTTPError } from "ky";
import { QueryObserverOptions } from "react-query/types/core/types";
import { useLocation } from "react-router-dom";

import {
  PropertyOrganization,
  DealerOrganization,
  IntercomModel,
  PaginatedResult,
  Intercom,
  IntercomComponent,
} from "src/common/types";
import { useKy } from "src/common/ky";

export const useIntercomsQuery = (enabled = true) => {
  const ky = useKy();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);

  if (!searchParams.has("pageNumber")) {
    searchParams.set("pageNumber", "1");
  }

  return useQuery<PaginatedResult<Intercom>>(
    ["INTERCOMS", searchParams.toString()],
    () =>
      ky.get("intercoms", { searchParams }).json<PaginatedResult<Intercom>>(),
    { enabled }
  );
};

export const GET_ALL_INTERCOM_MODELS_QUERY_KEY = ["INTERCOM_MODELS"];
export const useGetAllIntercomModelsQuery = (
  options: QueryObserverOptions<IntercomModel[], HTTPError> = {}
) => {
  const ky = useKy();
  return useQuery<IntercomModel[], HTTPError>(
    GET_ALL_INTERCOM_MODELS_QUERY_KEY,
    () => ky.get("product-models").json<IntercomModel[]>(),
    {
      staleTime: 60 * 1000,
      ...options,
    }
  );
};

export const GET_ALL_DEALER_ORGANIZATIONS_QUERY_KEY = ["DEALER_ORGANIZATIONS"];
export const useGetAllDealerOrganizationsQuery = (
  options: QueryObserverOptions<DealerOrganization[], HTTPError> = {}
) => {
  const ky = useKy();
  return useQuery<DealerOrganization[], HTTPError>(
    GET_ALL_DEALER_ORGANIZATIONS_QUERY_KEY,
    () => ky.get("dealer-organizations/all").json<DealerOrganization[]>(),
    {
      staleTime: 60 * 1000,
      ...options,
    }
  );
};

export const GET_ALL_PROPERTY_ORGANIZATIONS_QUERY_KEY = [
  "PROPERTY_ORGANIZATIONS",
];
export const useGetAllPropertyOrganizationsQuery = (
  options: QueryObserverOptions<PropertyOrganization[], HTTPError> = {}
) => {
  const ky = useKy();
  return useQuery<PropertyOrganization[], HTTPError>(
    GET_ALL_PROPERTY_ORGANIZATIONS_QUERY_KEY,
    () => ky.get("property-organizations/all").json<PropertyOrganization[]>(),
    {
      staleTime: 60 * 1000,
      ...options,
    }
  );
};

export const getIntercomQueryKey = (id: string) => ["INTERCOM", id];
export const useIntercomQuery = (intercomId: string) => {
  const ky = useKy();
  return useQuery<Intercom, HTTPError>(
    getIntercomQueryKey(intercomId),
    () => ky.get(`intercoms/${intercomId}`).json<Intercom>(),
    { staleTime: 60 * 1000 }
  );
};

export type RestartIntercomVariables = {
  intercomActivationId: string;
};

export const useRestartIntercomMutation = (
  onSuccess?: () => void,
  onError?: () => void
) => {
  const ky = useKy();
  return useMutation<void, HTTPError, RestartIntercomVariables>(
    async ({ intercomActivationId }) => {
      await ky.post(
        `product-unit-activations/${intercomActivationId}/reboot`,
        {}
      );
    },
    {
      onError,
      onSuccess,
    }
  );
};

export const usePowerCycleIntercomMutation = (
  onSuccess?: () => void,
  onError?: () => void
) => {
  const ky = useKy();
  return useMutation<void, HTTPError, RestartIntercomVariables>(
    async ({ intercomActivationId }) => {
      await ky.post(
        `product-unit-activations/${intercomActivationId}/power-cycle`,
        {}
      );
    },
    {
      onError,
      onSuccess,
    }
  );
};

export const useImportIntercomsMutation = (
  onSuccess?: (data: string[]) => void,
  onError?: (error: HTTPError) => void
) => {
  const ky = useKy();
  return useMutation<string[], HTTPError, FormData>(
    async (data) =>
      ky
        .post(`intercoms/import`, {
          // Headers are set automatically because this is a FormData object
          // https://github.com/sindresorhus/ky/issues/90
          body: data,
        })
        .json<string[]>(),
    {
      onError,
      onSuccess,
    }
  );
};

export const getProductActivationComponentsKey = (
  intercomActivationId?: string
) => ["INTERCOM_ACTIVATION", intercomActivationId, "COMPONENTS"];
export const useProductActivationComponentsQuery = (
  intercomActivationId: string
) => {
  const ky = useKy();
  return useQuery<IntercomComponent[], HTTPError>(
    getProductActivationComponentsKey(intercomActivationId),
    () =>
      ky
        .get(`product-unit-activations/${intercomActivationId}/components`)
        .json<IntercomComponent[]>()
  );
};
